import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from "axios";
import { API_BASE_URL, API_PREFIX } from "@/constants";
import { ACCESS_TOKEN_KEY, bearerTokenOf, endSession, renewAccessToken } from "./auth-tokens";

// Per-request opt-outs from the global redirect behavior. A page that wants
// to render its own error UI can pass these in the request config, e.g.:
//   apiClient.get("/projects/", { skipServerErrorRedirect: true })
// The interceptor below skips the corresponding window.location.replace
// so the caller's catch/allSettled branch can render inline.
declare module "axios" {
  export interface AxiosRequestConfig {
    skipServerErrorRedirect?: boolean;
    skipNotFoundRedirect?: boolean;
    // Set on the one retry that follows a token refresh, so a second 401 can never loop
    _authRetried?: boolean;
  }
}

// Server-error statuses handled by the dedicated error page.
const SERVER_ERROR_STATUSES = new Set([500, 502, 503]);

async function handleResponseError(client: AxiosInstance, error: AxiosError) {
  const config = error.config;
  const url: string = config?.url ?? "";
  const isAuthEndpoint = url.startsWith("/auth/");
  const status: number | undefined = error.response?.status;

  if (status === 401 && config && !isAuthEndpoint) {
    const sentToken = bearerTokenOf(config.headers?.Authorization);
    // A guest request (no token sent) refused with LOGIN_REQUIRED: nothing to refresh and no session to drop.
    if (!sentToken) return Promise.reject(error);

    if (!config._authRetried) {
      // Access token expired: renew it once (shared by concurrent 401s) and replay the request.
      try {
        await renewAccessToken(sentToken);
      } catch {
        // Cannot renew (no/expired/revoked refresh token, backend unreachable) -> sign out, as before.
        endSession();
        return Promise.reject(error);
      }
      // The request interceptor attaches the renewed token from localStorage.
      return client.request({ ...config, _authRetried: true });
    }
    // The renewed token was refused as well: the session is gone.
    endSession();
    return Promise.reject(error);
  }

  // Only redirect to /404 for non-auth endpoints — auth/SSO 404s are
  // meaningful application states (e.g. "no account yet, please sign up")
  // and must be handled by the caller so the user sees a friendly message.
  if (status === 404 && !isAuthEndpoint && !config?.skipNotFoundRedirect) {
    window.dispatchEvent(new CustomEvent("server:not-found"));
  }

  // Full-page redirect on 500/502/503 — friendly error screen instead of
  // an unhandled exception. Callers that want to render an INLINE error
  // (e.g. a dashboard with multiple widgets where only one fails) opt out
  // via `skipServerErrorRedirect: true` in the request config.
  if (
    status &&
    SERVER_ERROR_STATUSES.has(status) &&
    !isAuthEndpoint &&
    !config?.skipServerErrorRedirect
  ) {
    // Prevent redirect loops when the error page itself loads data.
    if (!window.location.pathname.startsWith("/server-error")) {
      window.dispatchEvent(new CustomEvent("server:error", { detail: { status } }));
    }
  }

  return Promise.reject(error);
}

// One axios instance = base URL + bearer token + the shared error/refresh handling above.
export function createApiClient(defaults: AxiosRequestConfig = {}): AxiosInstance {
  const client = axios.create({
    baseURL: `${API_BASE_URL}${API_PREFIX}`,
    headers: { "Content-Type": "application/json" },
    ...defaults,
  });

  client.interceptors.request.use((config) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  client.interceptors.response.use((response) => response, (error: AxiosError) => handleResponseError(client, error));

  return client;
}

export const apiClient = createApiClient();

// For services/navigator/*. Navigator screens draw their own loading / empty / error states
// ("찾을 수 없습니다 · 목록으로", inline retry …), so the global /404 and /server-error redirects are
// off by default here. A call can still opt back in with `skipNotFoundRedirect: false`.
// Token refresh and the 401 handling are shared with apiClient.
export const navigatorClient = createApiClient({ skipNotFoundRedirect: true, skipServerErrorRedirect: true });
